import { DateTime } from "luxon";
import React, { useContext } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import IconEvent from "../../../public/svg/icon-event.svg";
import { ThemeContext } from "../../contexts/ThemeContext";
import { getVariantTitle } from "../../ecommerce/product";
import { ExtendedCartMerchandise } from "../../types/shopify-components";
import { heading3, paragraph3 } from "../../utils/styles";

import EcommerceProductActions from "./EcommerceProductActions";
import LineItemTotal from "./LineItemTotal";
import { testIdFixedDay, testIdSpecialEvent } from "./Product";
import SingleProductLineItem from "./SingleProductLineItem";

interface ProductDefaultViewProps {
  isFixedSelection: boolean;
  selectedDate: DateTime | null;
  variantsToShowInLineItems: ExtendedCartMerchandise[];
  connectedVariants?: ExtendedCartMerchandise[];
  productTitle: string;
  lineItemTotalPrice: number;
  isChangingToProductsPage: boolean;
  specialEventTitle?: string;
  specialEventContent?: string;
}

const ProductDefaultView: React.FC<ProductDefaultViewProps> = ({
  connectedVariants,
  isFixedSelection,
  selectedDate,
  variantsToShowInLineItems,
  productTitle,
  lineItemTotalPrice,
  isChangingToProductsPage,
  specialEventTitle,
  specialEventContent,
}) => {
  const { styles } = useContext(ThemeContext);
  const { locale, formatMessage } = useIntl();
  return (
    <React.Fragment>
      <section className="pt4 mv4 bt b--moon-gray">
        {isFixedSelection && selectedDate && (
          <div className="mb4">
            <span className={`${styles.heading2}`} data-testid={testIdFixedDay}>
              {selectedDate && selectedDate.setLocale(locale).toLocaleString()}
            </span>
            {!!specialEventTitle && (
              <div
                className="mt4 pa3 ba b--pink"
                data-testid={testIdSpecialEvent}
              >
                <h3 className={`${heading3} mt0 mb4 flex items-center pink`}>
                  <span className="f0 mr2">
                    <IconEvent />
                  </span>
                  <span className="mt1">
                    <FormattedMessage id="product.specialEvents" />
                  </span>
                </h3>
                <ul className={`${paragraph3} list ma0 pa0`}>
                  <li className="flex">
                    <span
                      className="bg-pink br-100 dib flex-shrink-0 mt1"
                      aria-hidden="true"
                      style={{ width: "0.5rem", height: "0.5rem" }}
                    />
                    <span className="ml2">
                      {`${specialEventTitle}${
                        specialEventContent ? ` / ${specialEventContent}` : ""
                      }`}
                    </span>
                  </li>
                </ul>
              </div>
            )}
          </div>
        )}
        {!isFixedSelection || (isFixedSelection && selectedDate) ? (
          variantsToShowInLineItems.map(variant => {
            const title = getVariantTitle(productTitle, variant);
            const variantTitle =
              typeof title === "string"
                ? title
                : formatMessage(title.descriptor, title.values);

            return (
              <div key={variant.id} className="mt2 mb4 mb0-ns">
                <SingleProductLineItem
                  title={variantTitle}
                  merchandise={variant}
                  connectedMerchandises={connectedVariants}
                />
              </div>
            );
          })
        ) : (
          <div
            className={`${styles.paragraph3} nested-contained-copy-separator`}
          >
            <p>
              <FormattedMessage id="calendar.selectDateToSeeProducts" />
            </p>
          </div>
        )}
        <div className="mv4">
          <div className="pt3 bt b--moon-gray">
            <LineItemTotal totalPrice={lineItemTotalPrice} />
          </div>
        </div>
      </section>
      <EcommerceProductActions
        isChangingToProductsPage={isChangingToProductsPage}
        shouldNotify={isFixedSelection && !selectedDate}
        merchandises={variantsToShowInLineItems}
      />
    </React.Fragment>
  );
};

export default ProductDefaultView;
